// Discovery API schema types

export type DiscoveryStatus = "Discovered" | "Enriched";

export type TabId = "discover" | "selected" | "rejected";

export interface Influencer {
  id: string;
  handle: string;
  displayName: string;
  platform: string;
  bioText?: string;
  bioLink?: string;
  followersCount: number;
  followingCount: number;
  postCount: number;
  heartCount?: number;
  profileUrl?: string;
  profilePictureUrl?: string;
  isVerified: boolean;
  emails: string[];
  languageSignals: string[];
  countrySignals: string[];
  engagementRate?: number;
  avgLikes?: number;
  avgComments?: number;
  avgViews?: number;
  topics?: string[];
  hashtags?: string[];
  brandsMentioned?: string[];
  hasBrandDeals?: boolean;
  hasLinkInBio?: boolean;
  hasMerch?: boolean;
  isBusiness?: boolean;
  isCreator?: boolean;
  emailType?: string;
  followerGrowthRate?: number;
  postingFrequency?: number;
  // Full application data from DynamoDB (source of truth for display)
  applicationData?: ApplicationData;
  // Flattened ambassador fields from MeiliSearch (used for search/filtering)
  ambassadorBrands?: string[];
  ambassadorCountry?: string;
  ambassadorCity?: string;
  ambassadorAvailability?: string;
  ambassadorContentCount?: string;
  ambassadorOpenToAnyBrand?: boolean;
  discoveryStatus: DiscoveryStatus;
  discoveredAt: string;
}

export interface ApplicationData {
  tiktokUrl?: string;
  tiktokFollowers?: string;
  instagramUrl?: string;
  instagramFollowers?: string;
  brands: string[];
  openToAnyBrand: boolean;
  firstName: string;
  lastName: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  phoneCode: string;
  phone: string;
  email: string;
  availability: string;
  availabilityWhen?: string;
  contentCount: string;
  appliedAt: string;
}

export interface DiscoveryFilters {
  search: string;
  notSelected: boolean;
  minFollowers: number | null;
  engRateMin: number | null;
  countryCodes: string[];
  languages: string[];
  hasEmail: boolean | null;
  platform: string;
}

export interface PaginationState {
  pageIndex: number;
  pageSize: number;
}

export interface InfluencerList {
  id: string;
  name: string;
  description?: string;
  createdBy?: string;
  memberCount: number;
  createdAt: string;
  updatedAt?: string;
}

// Status badge colors
export const DISCOVERY_STATUS_COLORS: Record<
  DiscoveryStatus,
  { bg: string; dot: string; text: string }
> = {
  Discovered: { bg: "#FEF3C7", dot: "#F59E0B", text: "#92400E" },
  Enriched: { bg: "#DCFCE7", dot: "#16a34a", text: "#166534" },
};

// Region options (reused from V1)
export const REGION_OPTIONS = [
  { value: "US", label: "US", flag: "\u{1F1FA}\u{1F1F8}" },
  { value: "GB", label: "GB", flag: "\u{1F1EC}\u{1F1E7}" },
  { value: "KR", label: "KR", flag: "\u{1F1F0}\u{1F1F7}" },
  { value: "JP", label: "JP", flag: "\u{1F1EF}\u{1F1F5}" },
  { value: "AU", label: "AU", flag: "\u{1F1E6}\u{1F1FA}" },
  { value: "CA", label: "CA", flag: "\u{1F1E8}\u{1F1E6}" },
  { value: "DE", label: "DE", flag: "\u{1F1E9}\u{1F1EA}" },
  { value: "FR", label: "FR", flag: "\u{1F1EB}\u{1F1F7}" },
  { value: "SG", label: "SG", flag: "\u{1F1F8}\u{1F1EC}" },
  { value: "TH", label: "TH", flag: "\u{1F1F9}\u{1F1ED}" },
] as const;

// Min followers dropdown presets
export const MIN_FOLLOWERS_OPTIONS = [
  { value: null, label: "Any" },
  { value: 1000, label: "1,000" },
  { value: 5000, label: "5,000" },
  { value: 10000, label: "10,000" },
  { value: 50000, label: "50,000" },
  { value: 100000, label: "100,000" },
  { value: 500000, label: "500,000" },
  { value: 1000000, label: "1,000,000" },
] as const;

// Platform options
export const PLATFORM_OPTIONS = [
  { value: "tiktok", label: "TikTok" },
  { value: "instagram", label: "Instagram" },
  { value: "youtube", label: "YouTube" },
] as const;

// Language options
export const LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "ko", label: "Korean" },
  { value: "ja", label: "Japanese" },
  { value: "zh", label: "Chinese" },
  { value: "es", label: "Spanish" },
  { value: "fr", label: "French" },
  { value: "de", label: "German" },
  { value: "pt", label: "Portuguese" },
  { value: "th", label: "Thai" },
  { value: "vi", label: "Vietnamese" },
  { value: "id", label: "Indonesian" },
] as const;
